import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import Button from "@/components/atoms/Buttons/Button";
import Footer from "@/components/molecules/Footer";
import Header from "@/components/molecules/Header";
import { ReactComponent as ErrorIcon } from "@/assets/images/transactionError.svg";
import { ReactComponent as SuccessIcon } from "@/assets/images/transactionOk.svg";
import { ReactComponent as Spinner } from "@/assets/images/spinner.svg";
import { useTransactionData } from "@/context/transactionContext";
import i18n from "@/translate/i18n";
import { LayoutWrapper1xbet } from "@/components/molecules/Wrappers/LayoutWrapper1xbet";

const isValidUrl = (urlString) => {
  if (!urlString) return false;
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

const TIME_TO_REDIRECT = 5000;

function TransactionStatus1xbet() {
  const history = useHistory();
  const { id, token } = useParams();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const { transactionData } = useTransactionData();

  const isCancelledTransaction = transactionData?.status === "cancelled";

  const validSuccessUrl = useMemo(
    () =>
      isValidUrl(transactionData?.redirect_url_success)
        ? transactionData.redirect_url_success
        : null,
    [transactionData?.redirect_url_success],
  );

  const validRedirectUrl = useMemo(
    () =>
      isValidUrl(transactionData?.redirect_url)
        ? transactionData.redirect_url
        : null,
    [transactionData?.redirect_url],
  );

  const validRedirectUrlFailed = useMemo(
    () =>
      isValidUrl(transactionData?.redirect_url_failed)
        ? transactionData.redirect_url_failed
        : null,
    [transactionData?.redirect_url_failed],
  );

  useEffect(() => {
    const timer = setTimeout(() => setShowSuccessMessage(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isCancelledTransaction || !validRedirectUrlFailed) return undefined;
    const timer = setTimeout(() => {
      window.location.replace(validRedirectUrlFailed);
    }, TIME_TO_REDIRECT);
    return () => clearTimeout(timer);
  }, [isCancelledTransaction, validRedirectUrlFailed]);

  if (!transactionData?.id) return null;

  return (
    <div className="flex justify-center items-center bg-white sm:bg-gray-900 font-arboria-book min-h-screen overflow-x-hidden">
      <div className="flex flex-col justify-between items-center w-[500px] rounded-[20px] overflow-hidden text-center">
        <Header
          showAmount
          title={i18n.t("header.return")}
          handleClick={() =>
            history.replace(`/${id}/${token}${history.location.search}`)
          }
        />
        <LayoutWrapper1xbet className="!justify-center">
          <div className="flex flex-col items-center">
            {isCancelledTransaction ? (
              <>
                <ErrorIcon className="w-32" />
                <div className="mt-1">
                  <h2 className="text-black font-semibold text-2xl tracking-wider">
                    {i18n.t("error.title")}
                  </h2>
                </div>
              </>
            ) : showSuccessMessage ? (
              <>
                <SuccessIcon className="w-32" />
                <div className="mt-1">
                  <h2 className="text-black font-semibold text-2xl tracking-wider">
                    {transactionData?.type === "deposit"
                      ? i18n.t("success.title")
                      : i18n.t("transactionStatus.title")}
                  </h2>
                </div>
                <h3 className="mt-5 font-semibold text-lg text-light-gray">
                  {transactionData?.type === "deposit"
                    ? i18n.t("success.subtitle")
                    : i18n.t("transactionStatus.subtitle")}
                </h3>
                <h3 className="mt-5 font-medium text-md text-black">
                  {transactionData?.type === "deposit" ? (
                    <>
                      {i18n.t("success.info")}
                      <strong>{i18n.t("success.info2")}</strong>
                    </>
                  ) : (
                    <>
                      {i18n.t("transactionStatus.info")}
                      <strong>{i18n.t("transactionStatus.info2")}</strong>
                    </>
                  )}
                </h3>
                {validSuccessUrl && (
                  <Button
                    id="backToMerchant"
                    type="button"
                    handleClick={() => {
                      window.location.href = validSuccessUrl;
                    }}
                  >
                    {i18n.t("general.backTo", {
                      merchant: transactionData?.merchant?.name,
                    })}
                  </Button>
                )}
              </>
            ) : (
              <div className="h-48">
                <Spinner className="w-28" />
              </div>
            )}
          </div>
          <div>
            {validRedirectUrl && (
              <Button
                type="submit"
                handleClick={() => window.location.replace(validRedirectUrl)}
              >
                {i18n.t("confirmation.return")}
              </Button>
            )}
          </div>
        </LayoutWrapper1xbet>
        <Footer />
      </div>
    </div>
  );
}

export default TransactionStatus1xbet;
